import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Users, Briefcase, X, ChevronDown, Lock, Map } from 'lucide-react';
import { HomlyLogo } from '../../components/brand/HomlyLogo';

export function Navbar({ onDemoClick }) {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const isOnLanding = location.pathname === '/agences' || location.pathname === '/';

    // Navigation vers une section : scroll si déjà sur landing, sinon route vers /agences#<id>
    const goToSection = (sectionId) => (e) => {
        e.preventDefault();
        setMobileMenuOpen(false);
        if (isOnLanding) {
            const el = document.getElementById(sectionId);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            navigate(`/agences#${sectionId}`);
        }
    };
    
    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a1a]/95 backdrop-blur-md border-b border-white/10" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
                    <Link to="/">
                        <HomlyLogo size="md" />
                    </Link>
                    
                    <div className="hidden lg:flex items-center gap-5 xl:gap-8">
                        <a href="/agences#problems" onClick={goToSection('problems')} className="text-sm text-white/60 hover:text-white transition-colors font-medium whitespace-nowrap cursor-pointer">Pourquoi</a>
                        <a href="/agences#how" onClick={goToSection('how')} className="text-sm text-white/60 hover:text-white transition-colors font-medium whitespace-nowrap cursor-pointer">Comment ça marche</a>
                        <a href="/agences#ai" onClick={goToSection('ai')} className="text-sm text-white/60 hover:text-white transition-colors font-medium whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF914D] animate-pulse" />IA
                        </a>
                        <a href="/agences#pricing" onClick={goToSection('pricing')} className="text-sm text-white/60 hover:text-white transition-colors font-medium cursor-pointer">Tarifs</a>
                        <Link to="/roadmap" className="text-sm text-white/60 hover:text-white transition-colors font-medium flex items-center gap-1.5" data-testid="navbar-roadmap-link">
                            <Map className="w-3.5 h-3.5" />Roadmap
                        </Link>
                        <a href="/agences#faq" onClick={goToSection('faq')} className="text-sm text-white/60 hover:text-white transition-colors font-medium cursor-pointer">FAQ</a>
                        <span className="text-sm text-white/30 font-medium flex items-center gap-1 cursor-default">
                            <Lock className="w-3 h-3" />Réseau
                        </span>
                        <span className="text-sm text-white/30 font-medium flex items-center gap-1 cursor-default">
                            <Lock className="w-3 h-3" />Partenaires
                        </span>
                    </div>
                    
                    <div className="flex items-center gap-2 sm:gap-3">
                        <Link to="/login">
                            <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10 font-medium text-xs sm:text-sm px-2 sm:px-4">
                                Connexion
                            </Button>
                        </Link>
                        <Button 
                            onClick={onDemoClick}
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white font-bold shadow-lg shadow-[#681793]/30 text-sm px-4 sm:px-6"
                        >
                            <span className="hidden sm:inline">Demander ma démo</span>
                            <span className="sm:hidden">Démo</span>
                        </Button>
                        
                        <button 
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-2 rounded-lg hover:bg-white/10 text-white/70"
                        >
                            {mobileMenuOpen ? <X size={24} /> : <ChevronDown size={24} />}
                        </button>
                    </div>
                </div>
                
                {mobileMenuOpen && (
                    <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="lg:hidden py-4 border-t border-white/10"
                    >
                        <div className="flex flex-col gap-3">
                            <a href="/agences#problems" onClick={goToSection('problems')} className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer">Pourquoi Homly</a>
                            <a href="/agences#how" onClick={goToSection('how')} className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer">Comment ça marche</a>
                            <a href="/agences#ai" onClick={goToSection('ai')} className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors flex items-center gap-2 cursor-pointer">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#FF914D] animate-pulse" />IA
                            </a>
                            <a href="/agences#pricing" onClick={goToSection('pricing')} className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer">Tarifs</a>
                            <Link to="/roadmap" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors flex items-center gap-2" data-testid="navbar-mobile-roadmap-link">
                                <Map className="w-3.5 h-3.5" />Roadmap
                            </Link>
                            <a href="/agences#faq" onClick={goToSection('faq')} className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer">FAQ</a>
                            <span className="px-4 py-2 text-white/30 rounded-lg flex items-center gap-2 cursor-default">
                                <Lock className="w-3.5 h-3.5" />Réseau
                            </span>
                            <span className="px-4 py-2 text-white/30 rounded-lg flex items-center gap-2 cursor-default">
                                <Lock className="w-3.5 h-3.5" />Partenaires
                            </span>
                            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                                Connexion
                            </Link>
                        </div>
                    </motion.div>
                )}
            </div>
        </nav>
    );
}
